/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 07 2024
 **/

import { useTheme } from "@emotion/react";
import { useState } from "react";

import { _T } from "@rfice/language";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { Button, Column, Icon, TextField, Typography, useModal, useToast } from "@/frameworks/designStuff/components";

import { useAccount } from "@/frameworks/core/API";
import { APIErrorCode, ModalID, RegExp } from "@/frameworks/core/commons";
import { useAccountActions } from "@/frameworks/core/hooks";

export default function ChangePassword() {
	const toast = useToast();
	const { logout } = useAccountActions();
	const accountAPI = useAccount({
		onSuccess() {
			toast.show({ type: "system", message: _T("비밀번호가 변경되었습니다. 다시 로그인해주세요.") });
			modal.hide(ModalID.RESET_PASSWORD);
			logout();
		},
		onError(name, error: RestAPIError) {
			switch (name) {
				case "password:reset": {
					switch (error.code) {
						case APIErrorCode.USERNAME_OR_PASSWORD_NOT_MATCHED: {
							setResetPasswordError({
								errorType: "currentPassword",
								message: _T("현재 비밀번호를 다시 확인해 주세요.")
							});
						}
					}
				}
			}
		}
	});
	const [currentPassword, setCurrentPassword] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const modal = useModal();
	const { colors } = useTheme();
	const [resetPasswordError, setResetPasswordError] = useState({
		errorType: "",
		message: ""
	});

	const isValidPassword = () => {
		return RegExp.PASSWORD_REG.test(password);
	};

	const submitPassword = (ev: React.FormEvent<HTMLFormElement>) => {
		ev.preventDefault();
		if (!isValidPassword()) {
			setResetPasswordError({
				errorType: "password",
				message: _T("8자 이상 24자 이하 영문, 숫자, 특수문자를 혼합하여 사용해 주세요.")
			});
			return;
		} else {
			setResetPasswordError({
				errorType: "",
				message: _T("reset password error")
			});
		}
		if (password !== confirmPassword) {
			setResetPasswordError({
				errorType: "confirmPassword",
				message: _T("비밀번호를 다시 확인해 주세요.")
			});
			return;
		} else {
			setResetPasswordError({
				errorType: "",
				message: _T("reset password error")
			});
		}
		accountAPI.password.reset.execute({ oldPassword: currentPassword, newPassword: password });
	};

	const isAllInputFilled = currentPassword.length === 0 || password.length == 0 || confirmPassword.length === 0;

	return (
		<Column.form
			style={{ padding: "32px 44px", width: "400px", textAlign: "start" }}
			gap={20}
			onSubmit={submitPassword}
		>
			<Typography typoSize="Head5">{_T("비밀번호 변경")}</Typography>
			<Typography typoSize="Body3">{_T("현재 비밀번호를 입력해주세요.")}</Typography>
			<TextField.Icon
				placeholder={_T("현재 비밀번호")}
				type="password"
				autoComplete="current-password"
				onChange={(ev) => setCurrentPassword(ev.target.value)}
				error={resetPasswordError.errorType === "currentPassword"}
				help={resetPasswordError.errorType === "currentPassword" ? resetPasswordError.message : ""}
				leftIcon={<Icon name="Lock24" size={16} fill={colors.neutral_80_50} />}
			/>

			<Typography typoSize="Body3">{_T("새로운 비밀번호를 입력해주세요.")}</Typography>
			<Column gap={12}>
				<TextField.Icon
					placeholder={_T("비밀번호")}
					type="password"
					autoComplete="new-password"
					onChange={(ev) => setPassword(ev.target.value)}
					error={resetPasswordError.errorType === "password"}
					help={resetPasswordError.errorType === "password" ? resetPasswordError.message : ""}
					leftIcon={<Icon name="Lock24" size={16} fill={colors.neutral_80_50} />}
				/>
				<TextField.Icon
					placeholder={_T("비밀번호 확인")}
					type="password"
					autoComplete="new-password"
					onChange={(ev) => setConfirmPassword(ev.target.value)}
					leftIcon={<Icon name="Lock24" size={16} fill={colors.neutral_80_50} />}
					error={resetPasswordError.errorType === "confirmPassword"}
					help={resetPasswordError.errorType === "confirmPassword" ? resetPasswordError.message : ""}
				/>
			</Column>
			<Column gap={12} style={{ paddingTop: "4px", width: "100%" }}>
				<Button style={{ width: "100%" }} size="Xlarge" disabled={isAllInputFilled}>
					{_T("비밀번호 변경")}
				</Button>
				<Button
					filled="secondary"
					style={{ width: "100%" }}
					size="Xlarge"
					onClick={() => modal.hide(ModalID.RESET_PASSWORD)}
				>
					{_T("취소")}
				</Button>
			</Column>
		</Column.form>
	);
}
